<template>
  <PublicPageTemplate>
    <template
      v-if="displayBanner && !isMobile"
      v-slot:header-right
    >
      <div class="hidden md:block">
        <a
          class="text-white no-underline"
          href="https://www.signable.co.uk/signing-online-signature/"
          target="blank"
          data-ec="register"
          data-et="just_looking_to_sign_link">
          <div class="rounded-6 border-solid border-1 border-whiteOverride mr-xl p-base cursor-pointer hover:border-2">
            {{ $t('register.bannerText') }}
          </div>
        </a>
      </div>
    </template>
    <template
      v-slot:banner
      v-if="displayBanner && isMobile"
    >
      <div class="bg-bannerBlack p-base flex justify-center">
        <a
          class="text-white"
          href="https://www.signable.co.uk/signing-online-signature/"
          target="blank"
          data-ec="register"
          data-et="just_looking_to_sign_link_mobile">
          {{ $t('register.bannerText') }}
        </a>
      </div>
    </template>
    <template v-slot:content>
      <TWLoadingSpinner v-if="isProcessing" />
      <PublicPageHeader
        :title="titleText"
        :info="titleInfoText"
        class="mb-base mt-base"
      />
      <div
        v-if="!passwordOnly"
        class="mb-sm flex justify-end font-normal"
      >
        <span class="text-sm">
          {{ $t('register.requiredInformationNotice') }}
        </span>
      </div>
      <form @submit.prevent="submitForm">
        <FloatingLabelInput
          v-if="!passwordOnly"
          @input="clearErrorMessage('name')"
          v-model="formData.name"
          v-validate="'required'"
          :field-is-required="true"
          :has-error="errorMessages.name"
          :placeholder="$t('register.fullName')"
          :data-vv-as="$t('register.fullName')"
          type="text"
          data-vv-name="name"
          data-vv-scope="register"
          data-test="register-name"
          data-hj-suppress
          data-ec="register"
          data-et="name_field"
        />
        <PublicPageValidationError
          v-if="!passwordOnly"
          :error-message="errorMessages.name"
        />
        <FloatingLabelInput
          v-if="!passwordOnly"
          v-model.trim="formData.email"
          v-validate="'required|isTrimmedEmail'"
          @input="clearErrorMessage('email')"
          :field-is-required="true"
          :has-error="errorMessages.email"
          :placeholder="$t('register.emailAddress')"
          :data-vv-as="$t('register.emailAddress')"
          type="text"
          data-vv-name="email"
          data-vv-scope="register"
          data-test="register-email"
          data-hj-suppress
          data-ec="register"
          data-et="email_field"
        />
        <PublicPageValidationError
          v-if="!passwordOnly"
          :error-message="errorMessages.email"
        />
        <FloatingLabelInput
          v-if="!passwordOnly"
          v-model="formData.company_name"
          v-validate="'required'"
          @input="clearErrorMessage('company_name')"
          :field-is-required="true"
          :has-error="errorMessages.company_name"
          :placeholder="$t('register.companyName')"
          :data-vv-as="$t('register.companyName')"
          type="text"
          data-vv-name="company_name"
          data-vv-scope="register"
          data-test="register-company-name"
          data-hj-suppress
          data-ec="register"
          data-et="company-name-field"
        />
        <PublicPageValidationError
          v-if="!passwordOnly"
          :error-message="errorMessages.company_name"
        />
        <FloatingLabelDropdown
          v-if="!passwordOnly"
          v-model="formData.company_size"
          v-validate="'required'"
          @input="clearErrorMessage('company_size')"
          :options="companySizes"
          :has-error="!!errorMessages.company_size"
          :field-is-required="true"
          :placeholder="$t('register.companySize')"
          :class="{ 'select-error': !!errorMessages.company_size }"
          :data-vv-as="$t('register.companySize')"
          class="w-full"
          data-hj-suppress
          data-vv-scope="register"
          data-vv-name="company_size"
          data-test="register-company-size"
          data-ec="register"
          data-et="company-size-field"
        />
        <PublicPageValidationError
          v-if="!passwordOnly"
          :error-message="errorMessages.company_size"
        />
        <FloatingLabelInput
          v-if="!passwordOnly && displayPhone"
          v-model="formData.telephone"
          v-validate="'isPhoneNumber'"
          @input="clearErrorMessage('telephone')"
          :has-error="errorMessages.telephone"
          :placeholder="$t('register.telephone')"
          :data-vv-as="$t('register.telephone')"
          type="text"
          data-vv-name="telephone"
          data-vv-scope="register"
          data-test="register-telephone"
          data-hj-suppress
          data-ec="register"
          data-et="telephone-field"
        />
        <PublicPageValidationError
          v-if="!passwordOnly && displayPhone"
          :error-message="errorMessages.telephone"
        />
        <FloatingLabelInput
          v-model="formData.password"
          v-validate="'required|passwordGeneral'"
          @input="clearErrorMessage('password')"
          :field-is-required="!passwordOnly"
          :has-error="errorMessages.password"
          :placeholder="$t('register.password')"
          :data-vv-as="$t('register.password')"
          type="password"
          data-vv-name="password"
          data-vv-scope="register"
          data-test="register-password"
          data-hj-suppress
          autocomplete="new-password"
          data-ec="register"
          data-et="password-field"
        />
        <PublicPageValidationError
          :error-message="errorMessages.password"
        />
        <PublicPageSubmitButton
          :text="buttonText"
          data-test="register-button"
          data-ec="register"
          data-et="register_button"
        />
      </form>
      <div class="mb-sm font-normal">
        <span class="text-sm">
          {{ $t('register.blurb') }}
        </span>
      </div>
      <hr />
      <div class="text-center mb-xl text-darkFont">
        <i18n
         path="register.toLogin"
         tag="p"
        >
          <template v-slot:link>
            <RouterLink
              :to="{ name: 'Login' }"
              class="mt-base text-darkFont hover:text-primary active:text-darkFont font-normal"
              data-ec="register"
              data-et="login_link">
              {{ $t('register.login') }}
            </RouterLink>
          </template>
        </i18n>
      </div>
    </template>
  </PublicPageTemplate>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import TWLoadingSpinner from '@/components/tailwind/LoadingSpinner.vue';
import i18n from '@/locales';
import PublicPageTemplate from '@/components/tailwind/publicPages/PublicPageTemplate.vue';
import PublicPageHeader from '@/components/tailwind/publicPages/PublicPageHeader.vue';
import FloatingLabelInput from '@/components/tailwind/forms/FloatingLabelInput.vue';
import PublicPageSubmitButton from '@/components/tailwind/publicPages/PublicPageSubmitButton.vue';
import PublicPageValidationError from '@/components/tailwind/publicPages/PublicPageValidationError.vue';
import FloatingLabelDropdown from '@/components/tailwind/forms/FloatingLabelDropdown.vue';
import {
  findLocale,
  detectLocale,
} from '@/utils/locale';
import external from '@/utils/external';
import { EventBus } from '@/utils/event-bus';

const defaultCountryCode = 'GB';
const defaultErrorMessages = {
    name: null,
    email: null,
    company_name: null,
    company_size: null,
    telephone: null,
    password: null,
};

export default {
  name: 'RegisterPage',
  inject: {
    $validator: '$validator',
  },
  components: {
    FloatingLabelDropdown,
    PublicPageTemplate,
    FloatingLabelInput,
    TWLoadingSpinner,
    PublicPageHeader,
    PublicPageSubmitButton,
    PublicPageValidationError,
  },
  data() {
    return {
      passwordOnly: false,
      companySizes: ['1-9', '10-49', '50-249', '250+'],
      formData: {
        name: '',
        email: '',
        company_name: '',
        company_size: '',
        telephone: '',
        language: detectLocale(),
        password: '',
        country_code: defaultCountryCode,
        campaign: '',
      },
      displayBanner: true,
      displayPhone: true,
      isProcessing: false,
      errorMessages: defaultErrorMessages,
    };
  },
  created() {
    this.processQueryString();
    this.processEmbeddedWidget();
  },
  computed: {
    ...mapGetters({
      company: 'company/company',
      user: 'user/user',
    }),
    titleText() {
      return this.passwordOnly ? this.$t('register.passwordOnlyMessage') : this.$t('register.pageTitle');
    },
    titleInfoText() {
      return this.passwordOnly ? null : this.$t('register.info');
    },
    buttonText() {
      return this.passwordOnly ? this.$t('register.setPassword') : this.$t('register.signUp');
    },
  },
  methods: {
    ...mapActions({
      register: 'user/REGISTER',
    }),
    clearErrorMessage(type) {
      this.errorMessages[type] = null;
    },
    setLanguage(lang) {
      const language = findLocale(lang, detectLocale());
      this.formData.language = language;
      i18n.locale = language;
    },
    setCountryCode(code) {
      if (code && code.trim().match(/^([A-Z][A-Z])/gi)) {
        this.formData.country_code = code;
        return;
      }
      this.formData.country_code = defaultCountryCode;
    },
    trimmedValue(str) {
      return String(str).trim();
    },
    hasValue(str) {
      return this.trimmedValue(str) !== '';
    },
    async submitForm() {
      const validated = await this.$validator.validateAll('register');

      if (!validated) {
        this.errors.items.forEach((error) => {
          const { field, msg } = error;
          this.errorMessages[field] = msg;
        });
        return;
      }

      try {
        this.isProcessing = true;

        await this.register(this.formData);

        external.trackRegistration({
          company: this.company,
          campaign: this.formData.campaign,
          user: this.user,
        });

        try {
          await this.$router.push({
            name: 'Envelopes',
          });
        } catch (e) {
          window.location.href = '/';
        }
      } catch (e) {
        this.isProcessing = false;

        EventBus.$emit('renderGlobalMessage', ({
          message: e.message,
          icon: 'times',
          type: 'error',
        }));

        /**
         * Iterate over the errors returned from the API
         * and set on the form itself rather than through
         * notifications.
         */
        if (e.errors) {
          // eslint-disable-next-line no-restricted-syntax
          for (const [key, value] of Object.entries(e.errors)) {
            // eslint-disable-next-line prefer-destructuring
            this.errorMessages[key] = value[0];
          }
        }

        // We have errors, so we'll display the complete form
        this.passwordOnly = false;
      }
    },
    processQueryString() {
      // Check if we have an querystring data
      if (!Object.keys(this.$route.query).length) {
        return;
      }

      const { query } = this.$route;

      // Populate fields
      if (this.hasValue(query.language)) {
        this.setLanguage(query.language);
      }
      if (this.hasValue(query.country_code)) {
        this.setCountryCode(query.country_code);
      }
      if (this.hasValue(query.campaign)) {
        this.formData.campaign = this.trimmedValue(query.campaign);
      }
      if (this.trimmedValue(query.no_phone) === 'true') {
        this.displayPhone = false;
      }
      if (this.trimmedValue(query.no_notice) === 'true') {
        this.displayBanner = false;
      }
    },
    processEmbeddedWidget() {
      // Populate form if we have data from the embedded registration widget
      let embeddedRegistration = document.cookie.split(';')
        .map(cookie => cookie.trim().split('='))
        .filter(item => item[0] === 'register_widget')
        .map(item => item[1])
        .pop();

      if (embeddedRegistration) {
        try {
          embeddedRegistration = JSON.parse(embeddedRegistration);
          this.formData.name = this.trimmedValue(embeddedRegistration.name);
          this.formData.email = this.trimmedValue(embeddedRegistration.email);
          this.formData.company_name = this.trimmedValue(embeddedRegistration.company_name);
          this.formData.company_size = this.trimmedValue(embeddedRegistration.company_size);
          this.formData.telephone = this.trimmedValue(embeddedRegistration.phone);
          this.formData.campaign = this.trimmedValue(embeddedRegistration.campaign);
          this.setLanguage(embeddedRegistration.language);
          this.setCountryCode(embeddedRegistration.country_code);
          // If the embedded widget displays the phone, we don't want to show
          // it again for them.
          this.displayPhone = !embeddedRegistration.displayPhone;
          // User has arrived from a landing page, no need to display
          // the banner
          this.displayBanner = false;
          // Delete the register widget cookie
          document.cookie = 'register_widget=; max-age=-1';
          this.passwordOnly = true;
        } catch (e) {
          // eslint-disable-next-line
          console.error(e);
        }
      }
    },
  },
};
</script>

